import { create } from 'zustand';
import type {
  TermPlan,
  TermCalculation,
  TimeLineSegment,
  GroupCost,
  PaxCost,
} from './columbusTypes';
import type {
  LoadColumbusPlannerResponse,
  ColumbusPlannerTermModel,
} from '@/api/generated/schemas';
import { recomputeTerm, emptyPlan } from './columbusPlans';

interface ColumbusState {
  timeLineId: number | null;
  dateFrom: string | null;
  dateTo: string | null;
  segments: TimeLineSegment[];

  /** Sdílená jména group-costů přes všechny plány */
  groupCosts: Pick<GroupCost, 'name'>[];
  /** Sdílená jména pax-costů přes všechny plány */
  paxCosts: Pick<PaxCost, 'name'>[];
  /** Plány indexované klíčem: `term:{termId}` nebo `color:{colorCbId}` nebo `default` */
  plans: Record<string, TermPlan>;
  terms: ColumbusPlannerTermModel[];
  calculations: TermCalculation[];
  maxTariffsCount: number;
  isForTestVersion: boolean;

  /** Revize časové osy – inkrementuje se při každé změně dateFrom/dateTo/segments */
  timelineRevision: number;
  /** Revize per-plán – klíč = planKey, hodnota = počet změn daného plánu */
  planRevisions: Record<string, number>;

  initData: (response: LoadColumbusPlannerResponse) => void;

  addSegment: (next: number) => void;
  updateSegmentColor: (next: number, colorCbId: number | null) => void;
  removeSegment: (next: number) => void;
  setAllSegments: (newSegments: TimeLineSegment[]) => void;
  setTimeLineDates: (dateFrom: string, dateTo: string) => void;

  updateTariff: (
    planKey: string,
    index: number,
    field: 'pax' | 'retailPrice',
    value: number | null
  ) => void;
  addTariff: (planKey: string) => void;
  removeTariff: (planKey: string, index: number) => void;

  addGroupCost: () => void;
  updateGroupCostName: (index: number, name: string) => void;
  updateGroupCostPrice: (planKey: string, index: number, price: number | null) => void;
  removeGroupCost: (index: number) => void;

  addPaxCost: () => void;
  updatePaxCostName: (index: number, name: string) => void;
  updatePaxCostPrice: (planKey: string, index: number, price: number | null) => void;
  removePaxCost: (index: number) => void;

  updateTicketPrice: (
    planKey: string,
    field: 'depTicketPrice' | 'retTicketPrice',
    value: number | null
  ) => void;

  updateTerm: (termModel: ColumbusPlannerTermModel) => void;
}

function buildPlanKey(termId: number | null, colorCbId: number | null): string {
  if (termId != null) return `term:${termId}`;
  if (colorCbId != null) return `color:${colorCbId}`;
  return 'default';
}

/** Vypočítá dateFrom a dateTo časové osy jako týden před/za min/max datem termínů. */
function computeTimelineDates(terms: ColumbusPlannerTermModel[]): {
  dateFrom: string | null;
  dateTo: string | null;
} {
  if (terms.length === 0) return { dateFrom: null, dateTo: null };
  let minMs = Infinity;
  let maxMs = -Infinity;
  for (const term of terms) {
    const fromMs = new Date(term.dateFrom).getTime();
    minMs = Math.min(minMs, fromMs);
    const endMs =
      term.dateTo != null
        ? new Date(term.dateTo).getTime()
        : term.nights != null && term.nights > 0
          ? fromMs + term.nights * 86400000
          : fromMs;
    maxMs = Math.max(maxMs, endMs);
  }
  const from = new Date(minMs - 7 * 86400000);
  const to = new Date(maxMs + 7 * 86400000);
  return {
    dateFrom: from.toISOString().split('T')[0]!,
    dateTo: to.toISOString().split('T')[0]!,
  };
}

/** Vrátí colorCbId segmentu, do jehož pásma termín svým dateFrom spadá. */
export function computeColorCbId(
  termDateFrom: string,
  timelineDateFrom: string | null,
  segments: TimeLineSegment[]
): number | null {
  if (!timelineDateFrom || segments.length === 0) return null;
  const dayOffset = Math.round(
    (new Date(termDateFrom).getTime() - new Date(timelineDateFrom).getTime()) / 86400000
  );
  const sorted = [...segments].sort((a, b) => a.next - b.next);
  return sorted.find((s) => dayOffset < s.next)?.colorCbId ?? null;
}

function recomputeAll(
  terms: ColumbusPlannerTermModel[],
  plans: Record<string, TermPlan>,
  dateFrom: string | null,
  segments: TimeLineSegment[]
): TermCalculation[] {
  return terms.map((term) => {
    const key = `term:${term.id}`;
    const colorCbId = computeColorCbId(term.dateFrom, dateFrom, segments);
    const colorKey = colorCbId != null ? `color:${colorCbId}` : 'default';
    const plan = plans[key] ??
      plans[colorKey] ??
      plans['default'] ?? {
        depTicketPrice: null,
        retTicketPrice: null,
        groupCosts: [],
        paxCosts: [],
        tariffs: [],
      };
    return recomputeTerm(term, plan);
  });
}

/** Inkrementuje revizi jednoho plánu */
function bumpPlanRev(
  planRevisions: Record<string, number>,
  planKey: string
): Record<string, number> {
  return { ...planRevisions, [planKey]: (planRevisions[planKey] ?? 0) + 1 };
}

/** Inkrementuje revize všech existujících plánů (pro mutace ovlivňující všechny plány) */
function bumpAllPlanRevs(
  planRevisions: Record<string, number>,
  plans: Record<string, TermPlan>
): Record<string, number> {
  const result = { ...planRevisions };
  for (const key of Object.keys(plans)) {
    result[key] = (result[key] ?? 0) + 1;
  }
  return result;
}

export const useColumbusStore = create<ColumbusState>()((set, _get) => ({
  timeLineId: null,
  dateFrom: null,
  dateTo: null,
  segments: [],
  groupCosts: [],
  paxCosts: [],
  plans: {},
  terms: [],
  calculations: [],
  maxTariffsCount: 3,
  isForTestVersion: false,
  timelineRevision: 0,
  planRevisions: {},

  initData(response) {
    const tl = response.timeLine;
    const segments: TimeLineSegment[] = tl?.segments ?? [];

    const { dateFrom, dateTo } = computeTimelineDates(response.terms);
    const datesDiffer =
      dateFrom !== (tl?.dateFrom ?? dateFrom) || dateTo !== (tl?.dateTo ?? dateTo);

    const firstPlanData = response.settings.length > 0 ? response.settings[0]!.planData : null;

    const groupCostNames: Pick<GroupCost, 'name'>[] =
      firstPlanData?.groupCosts.map((gc) => ({ name: gc.name })) ?? [];
    const paxCostNames: Pick<PaxCost, 'name'>[] =
      firstPlanData?.paxCosts.map((pc) => ({ name: pc.name })) ?? [];

    const plans: Record<string, TermPlan> = {};
    for (const setting of response.settings) {
      const key = buildPlanKey(setting.termId, setting.colorCbId);
      plans[key] = setting.planData;
    }

    set({
      timeLineId: tl?.id ?? null,
      dateFrom,
      dateTo,
      segments,
      groupCosts: groupCostNames,
      paxCosts: paxCostNames,
      plans,
      terms: response.terms,
      calculations: recomputeAll(response.terms, plans, dateFrom, segments),
      maxTariffsCount: response.maxTariffsCount,
      isForTestVersion: response.isForTestVersion,
      timelineRevision: datesDiffer ? 1 : 0,
      planRevisions: {},
    });
  },

  // --- Timeline mutace ---

  addSegment(next) {
    set((state) => {
      const segments = [...state.segments, { next, colorCbId: null }].sort(
        (a, b) => a.next - b.next
      );
      return {
        segments,
        timelineRevision: state.timelineRevision + 1,
        calculations: recomputeAll(state.terms, state.plans, state.dateFrom, segments),
      };
    });
  },

  updateSegmentColor(next, colorCbId) {
    set((state) => {
      const segments = state.segments.map((s) => (s.next === next ? { ...s, colorCbId } : s));
      return {
        segments,
        timelineRevision: state.timelineRevision + 1,
        calculations: recomputeAll(state.terms, state.plans, state.dateFrom, segments),
      };
    });
  },

  removeSegment(next) {
    set((state) => {
      const segments = state.segments.filter((s) => s.next !== next);
      return {
        segments,
        timelineRevision: state.timelineRevision + 1,
        calculations: recomputeAll(state.terms, state.plans, state.dateFrom, segments),
      };
    });
  },

  setAllSegments(newSegments) {
    set((state) => ({
      segments: newSegments,
      timelineRevision: state.timelineRevision + 1,
      calculations: recomputeAll(state.terms, state.plans, state.dateFrom, newSegments),
    }));
  },

  setTimeLineDates(dateFrom, dateTo) {
    set((state) => ({
      dateFrom,
      dateTo,
      timelineRevision: state.timelineRevision + 1,
      calculations: recomputeAll(state.terms, state.plans, dateFrom, state.segments),
    }));
  },

  // --- Per-planKey mutace ---

  updateTariff(planKey, index, field, value) {
    set((state) => {
      const plan =
        state.plans[planKey] ??
        emptyPlan(
          state.groupCosts as GroupCost[],
          state.paxCosts as PaxCost[],
          state.maxTariffsCount
        );
      const tariffs = [...plan.tariffs];
      tariffs[index] = { ...tariffs[index]!, [field]: value } as (typeof tariffs)[number];
      const updatedPlan = { ...plan, tariffs };
      const plans = { ...state.plans, [planKey]: updatedPlan };
      return {
        plans,
        calculations: recomputeAll(state.terms, plans, state.dateFrom, state.segments),
        planRevisions: bumpPlanRev(state.planRevisions, planKey),
      };
    });
  },

  addTariff(planKey) {
    set((state) => {
      const plan =
        state.plans[planKey] ??
        emptyPlan(
          state.groupCosts as GroupCost[],
          state.paxCosts as PaxCost[],
          state.maxTariffsCount
        );
      const tariffs = [...plan.tariffs, { pax: null, retailPrice: null }];
      const updatedPlan = { ...plan, tariffs };
      const plans = { ...state.plans, [planKey]: updatedPlan };
      return {
        plans,
        calculations: recomputeAll(state.terms, plans, state.dateFrom, state.segments),
        planRevisions: bumpPlanRev(state.planRevisions, planKey),
      };
    });
  },

  removeTariff(planKey, index) {
    set((state) => {
      const plan = state.plans[planKey];
      if (!plan) return {};
      const tariffs = plan.tariffs.filter((_, i) => i !== index);
      const updatedPlan = { ...plan, tariffs };
      const plans = { ...state.plans, [planKey]: updatedPlan };
      return {
        plans,
        calculations: recomputeAll(state.terms, plans, state.dateFrom, state.segments),
        planRevisions: bumpPlanRev(state.planRevisions, planKey),
      };
    });
  },

  updateGroupCostPrice(planKey, index, price) {
    set((state) => {
      const plan =
        state.plans[planKey] ??
        emptyPlan(
          state.groupCosts as GroupCost[],
          state.paxCosts as PaxCost[],
          state.maxTariffsCount
        );
      const groupCosts = plan.groupCosts.map((gc, i) => (i === index ? { ...gc, price } : gc));
      const updatedPlan = { ...plan, groupCosts };
      const plans = { ...state.plans, [planKey]: updatedPlan };
      return {
        plans,
        calculations: recomputeAll(state.terms, plans, state.dateFrom, state.segments),
        planRevisions: bumpPlanRev(state.planRevisions, planKey),
      };
    });
  },

  updatePaxCostPrice(planKey, index, price) {
    set((state) => {
      const plan =
        state.plans[planKey] ??
        emptyPlan(
          state.groupCosts as GroupCost[],
          state.paxCosts as PaxCost[],
          state.maxTariffsCount
        );
      const paxCosts = plan.paxCosts.map((pc, i) => (i === index ? { ...pc, price } : pc));
      const updatedPlan = { ...plan, paxCosts };
      const plans = { ...state.plans, [planKey]: updatedPlan };
      return {
        plans,
        calculations: recomputeAll(state.terms, plans, state.dateFrom, state.segments),
        planRevisions: bumpPlanRev(state.planRevisions, planKey),
      };
    });
  },

  updateTicketPrice(planKey, field, value) {
    set((state) => {
      const plan =
        state.plans[planKey] ??
        emptyPlan(
          state.groupCosts as GroupCost[],
          state.paxCosts as PaxCost[],
          state.maxTariffsCount
        );
      const updatedPlan = { ...plan, [field]: value };
      const plans = { ...state.plans, [planKey]: updatedPlan };
      return {
        plans,
        calculations: recomputeAll(state.terms, plans, state.dateFrom, state.segments),
        planRevisions: bumpPlanRev(state.planRevisions, planKey),
      };
    });
  },

  // --- Mutace ovlivňující všechny plány ---

  addGroupCost() {
    set((state) => {
      const newName = { name: '' };
      const groupCosts = [...state.groupCosts, newName];
      const plans = Object.fromEntries(
        Object.entries(state.plans).map(([k, plan]) => [
          k,
          { ...plan, groupCosts: [...plan.groupCosts, { name: '', price: null }] },
        ])
      );
      return {
        groupCosts,
        plans,
        planRevisions: bumpAllPlanRevs(state.planRevisions, state.plans),
      };
    });
  },

  updateGroupCostName(index, name) {
    set((state) => {
      const groupCosts = state.groupCosts.map((gc, i) => (i === index ? { name } : gc));
      const plans = Object.fromEntries(
        Object.entries(state.plans).map(([k, plan]) => [
          k,
          {
            ...plan,
            groupCosts: plan.groupCosts.map((gc, i) => (i === index ? { ...gc, name } : gc)),
          },
        ])
      );
      return {
        groupCosts,
        plans,
        planRevisions: bumpAllPlanRevs(state.planRevisions, state.plans),
      };
    });
  },

  removeGroupCost(index) {
    set((state) => {
      const groupCosts = state.groupCosts.filter((_, i) => i !== index);
      const plans = Object.fromEntries(
        Object.entries(state.plans).map(([k, plan]) => [
          k,
          { ...plan, groupCosts: plan.groupCosts.filter((_, i) => i !== index) },
        ])
      );
      return {
        groupCosts,
        plans,
        calculations: recomputeAll(state.terms, plans, state.dateFrom, state.segments),
        planRevisions: bumpAllPlanRevs(state.planRevisions, state.plans),
      };
    });
  },

  addPaxCost() {
    set((state) => {
      const paxCosts = [...state.paxCosts, { name: '' }];
      const plans = Object.fromEntries(
        Object.entries(state.plans).map(([k, plan]) => [
          k,
          { ...plan, paxCosts: [...plan.paxCosts, { name: '', price: null }] },
        ])
      );
      return { paxCosts, plans, planRevisions: bumpAllPlanRevs(state.planRevisions, state.plans) };
    });
  },

  updatePaxCostName(index, name) {
    set((state) => {
      const paxCosts = state.paxCosts.map((pc, i) => (i === index ? { name } : pc));
      const plans = Object.fromEntries(
        Object.entries(state.plans).map(([k, plan]) => [
          k,
          {
            ...plan,
            paxCosts: plan.paxCosts.map((pc, i) => (i === index ? { ...pc, name } : pc)),
          },
        ])
      );
      return { paxCosts, plans, planRevisions: bumpAllPlanRevs(state.planRevisions, state.plans) };
    });
  },

  removePaxCost(index) {
    set((state) => {
      const paxCosts = state.paxCosts.filter((_, i) => i !== index);
      const plans = Object.fromEntries(
        Object.entries(state.plans).map(([k, plan]) => [
          k,
          { ...plan, paxCosts: plan.paxCosts.filter((_, i) => i !== index) },
        ])
      );
      return {
        paxCosts,
        plans,
        calculations: recomputeAll(state.terms, plans, state.dateFrom, state.segments),
        planRevisions: bumpAllPlanRevs(state.planRevisions, state.plans),
      };
    });
  },

  updateTerm(termModel) {
    set((state) => {
      const terms = state.terms.map((t) =>
        t.id === termModel.id ? { ...termModel, colorCbId: t.colorCbId } : t
      );
      return {
        terms,
        calculations: recomputeAll(terms, state.plans, state.dateFrom, state.segments),
      };
    });
  },
}));
